import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Lock,
  Unlock,
  Shield,
  Upload,
  Send,
  Fingerprint,
  CheckCircle2,
  XCircle,
  Play,
  ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCredentialAnimation } from './CredentialAnimations';

/**
 * Animation Demo Page
 * Showcases all credential animations for testing and demonstration
 */
export const AnimationDemo = () => {
  const animations = useCredentialAnimation();
  const [lastAnimation, setLastAnimation] = useState(null);
  
  const demoAnimations = [
    {
      id: 'encrypt',
      label: 'Encrypt Credential',
      description: 'AES-256 encryption animation with rotating rings and data particles',
      icon: Lock,
      color: 'neon-cyan',
      action: () => animations.encrypt({
        title: "Encrypting Credential",
        subtitle: "256-bit AES encryption in progress"
      })
    },
    {
      id: 'verify',
      label: 'Verify Document',
      description: 'Document scanning and verification with progress steps',
      icon: Shield,
      color: 'neon-green',
      action: () => animations.verify({
        title: "Verifying Credential",
        success: true
      })
    },
    {
      id: 'transfer',
      label: 'Transfer Credential',
      description: 'Secure file transfer with packet visualization',
      icon: Send,
      color: 'neon-purple',
      action: () => animations.transfer({
        title: "Transferring Credential",
        fromLabel: "Your Vault",
        toLabel: "Provider"
      })
    },
    {
      id: 'lock',
      label: 'Lock Vault',
      description: 'Bank vault lock animation with rotating tumblers',
      icon: Lock,
      color: 'yellow-500',
      action: () => animations.lock({
        title: "Securing Credential Vault"
      })
    },
    {
      id: 'unlock',
      label: 'Unlock Vault',
      description: 'Vault unlock animation for credential access',
      icon: Unlock,
      color: 'blue-500',
      action: () => animations.unlock({
        title: "Accessing Secure Vault"
      })
    },
    {
      id: 'scan',
      label: 'Biometric Scan',
      description: 'Fingerprint scanning with verification pulse',
      icon: Fingerprint,
      color: 'orange-500',
      action: () => animations.scan({
        title: "Biometric Verification",
        success: true
      })
    },
    {
      id: 'approve',
      label: 'Approve Request',
      description: 'Success animation with burst effect',
      icon: CheckCircle2,
      color: 'neon-green',
      action: () => animations.approve({
        title: "Credential Approved!"
      })
    },
    {
      id: 'reject',
      label: 'Reject Request',
      description: 'Rejection animation with visual feedback',
      icon: XCircle,
      color: 'red-500',
      action: () => animations.reject({
        title: "Credential Rejected"
      })
    }
  ];
  
  const handleDemo = async (demo) => {
    setLastAnimation(demo.id);
    await demo.action();
    console.log(`${demo.label} animation completed`);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8">
      {/* Animation Components */}
      <animations.AnimationComponent />
      
      {/* Header */}
      <div className="max-w-4xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center text-slate-400 hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>
        
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-heading font-bold text-white mb-4">
            Credential Animations
          </h1>
          <p className="text-xl text-slate-400">
            Immersive animations for credential management actions
          </p>
          <p className="text-sm text-neon-cyan mt-2">
            Click any card to see the animation in action
          </p>
        </motion.div>
        
        {/* Animation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {demoAnimations.map((demo, index) => (
            <motion.button
              key={demo.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => handleDemo(demo)}
              disabled={animations.isAnimating}
              className={`
                group relative glass-card p-6 text-left transition-all
                hover:border-${demo.color}/50 hover:shadow-lg hover:shadow-${demo.color}/10
                ${animations.isAnimating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                ${lastAnimation === demo.id ? `border-${demo.color}/50` : ''}
              `}
            >
              <div className="flex items-start justify-between">
                <div className={`w-14 h-14 rounded-xl bg-${demo.color}/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <demo.icon className={`w-7 h-7 text-${demo.color}`} />
                </div>
                <div className={`w-10 h-10 rounded-full bg-white/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity`}>
                  <Play className="w-5 h-5 text-white" />
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-white mb-2">
                {demo.label}
              </h3>
              <p className="text-sm text-slate-400">
                {demo.description}
              </p>
              
              {lastAnimation === demo.id && (
                <div className="absolute top-4 right-4">
                  <span className="text-xs text-neon-green">Last played</span>
                </div>
              )}
            </motion.button>
          ))}
        </div>
        
        {/* Use Cases */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-12 glass-card p-8"
        >
          <h2 className="text-xl font-bold text-white mb-6">
            When to Use These Animations
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-neon-cyan font-medium mb-3">Driver Portal</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>• <span className="text-white">Upload Credential</span> → Encrypt + Lock</li>
                <li>• <span className="text-white">Share with Provider</span> → Verify + Transfer</li>
                <li>• <span className="text-white">View Secure Doc</span> → Unlock + Scan</li>
                <li>• <span className="text-white">Approve Request</span> → Scan + Approve</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-neon-purple font-medium mb-3">Provider Portal</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>• <span className="text-white">Request Credential</span> → Transfer</li>
                <li>• <span className="text-white">Verify Driver</span> → Verify + Approve/Reject</li>
                <li>• <span className="text-white">Add to Trust Network</span> → Lock</li>
                <li>• <span className="text-white">Background Check</span> → Scan + Verify</li>
              </ul>
            </div>
          </div>
        </motion.div>
        
        {/* Integration Code */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-8 glass-card p-8"
        >
          <h2 className="text-xl font-bold text-white mb-4">
            How to Use
          </h2>
          
          <pre className="bg-slate-900 rounded-lg p-4 text-sm text-slate-300 overflow-x-auto">
{`import { useCredentialAnimation } from './CredentialAnimations';

const MyComponent = () => {
  const animations = useCredentialAnimation();
  
  const handleUpload = async () => {
    // Show encryption animation
    await animations.encrypt({ title: "Securing Your File" });
    
    // Show vault lock animation
    await animations.lock({ title: "Storing Securely" });
    
    // Continue with actual upload...
  };
  
  return (
    <>
      <animations.AnimationComponent />
      <button onClick={handleUpload}>Upload</button>
    </>
  );
};`}
          </pre>
        </motion.div>
      </div>
    </div>
  );
};

export default AnimationDemo;
